import flatpickr from "flatpickr";

import {
    FindingsTableItem,
    ScanTableData,
    ProjectTableData,
    PackageTableData,
    FindingsGroup
} from "../../models/tables";
import {
  FilterData,
  FilterItem,
  OptionItem,
  CommonFilterData,
} from "../../models/filters";
import { availableFilters } from "../constants";

export class FilterTable {
  filterData: CommonFilterData[] = [];
  filters: FilterData;

  constructor(filters: FilterData) {
    this.filters = filters;
  }

  // Set filter options from fetched table data
  setFilterData(
    tableData: (
      | FindingsTableItem
      | ScanTableData
      | ProjectTableData
      | PackageTableData
    )[]
  ) {
    this.filterData = this.filters.filters.map((item: FilterItem) => {
      let availableFiltersCopy = { ...availableFilters };
      if (
        this.filters.table === "Scans" ||
        this.filters.table === "Codebase Scans"
      ) {
        availableFiltersCopy = {
          ...availableFiltersCopy,
          type: availableFiltersCopy.scan_type,
        };
      }
      if (this.filters.table === "Codebase Packages") {
        availableFiltersCopy = {
          ...availableFiltersCopy,
          ["risk" as keyof typeof availableFilters]:
            availableFiltersCopy.risk_score,
        };
      }
      return {
        ...item,
        options:
          item.col === "project" || item.col === "branch_name"
            ? [
                ...new Set(
                  tableData.map((dataItem) => {
                    if (item.col === "project") {
                      return "project" in dataItem && dataItem.project
                        ? dataItem.project.name
                        : "";
                    } else if (item.col === "branch_name") {
                      return "branch_name" in dataItem ? dataItem.branch_name : "";
                    }
                    return "";
                  })
                ),
              ]
                .filter((item) => item !== "")
                .sort((a: string, b: string) => (a < b ? -1 : 1))
                .map((item) => {
                  return {
                    label: item,
                    value: item,
                    checked: false,
                  };
                })
            : item.col === "date"
            ? availableFiltersCopy[
                item.col as keyof typeof availableFiltersCopy
              ]
            : availableFiltersCopy[
                item.col as keyof typeof availableFiltersCopy
              ].filter((filterItem) =>
                !!tableData.find((row) => {
                  if ("risk_score" in row && item.col === "risk_score") {
                    return (
                      row.risk_score &&
                      row.risk_score.label.toLowerCase() ===
                        filterItem.value.toLowerCase()
                    );
                  }
                  if ("risk" in row && row.risk && item.col === "risk") {
                    return (
                      row.risk.category?.toLowerCase() ===
                      filterItem.value.toLowerCase()
                    );
                  }
                  if ("ecosystems" in row && item.col === "ecosystems") {
                    return row.ecosystems.includes(filterItem.value);
                  }
                  if ("findings" in row && item.col === "findings") {
                    return (
                      (row.findings.groups[
                        filterItem.value as keyof FindingsGroup
                      ] || 0) > 0
                    );
                  }
                  return (
                    row[item.col as keyof typeof row] === filterItem.value
                  );
                })
              ),
      };
    });
  }

  // When option is selected as Only
  selectOnlyOneOption(col: string, option: OptionItem) {
    return (this.filterData = this.filterData.map((filter) => {
      if (filter.col === col) {
        return {
          ...filter,
          options: filter.options.map((item) => {
            const checkboxElement: HTMLInputElement | null =
              document.getElementById(item.value) as HTMLInputElement;
            if (checkboxElement)
              checkboxElement.checked =
                item.value === option.value ? true : false;
            return {
              ...item,
              checked: item.label === option.label ? true : false,
              from:
                item.label === "Select from calendar" ? option.from : item.from,
              to: item.label === "Select from calendar" ? option.to : item.to,
            };
          }),
        };
      } else return filter;
    }));
  }

  // Reset all filters
  clearFilters(col: string) {
    this.filterData = this.filterData.map((item: CommonFilterData) => {
      if (item.col === col) {
        item.options.forEach((option) => {
          const targetCheckbox = document.getElementById(
            option.value
          ) as HTMLInputElement;
          if (targetCheckbox) {
            targetCheckbox.checked = false;
          }
        });
        return {
          ...item,
          options: item.options.map((option: OptionItem) => {
            return { ...option, checked: false };
          }),
        };
      }
      return item;
    });
  }
}

export class IndividualFilter {
  open: boolean = false;
  $refs: any;
  selectedFilters: OptionItem[] = [];

  // Toggle dropdown
  toggle() {
    if (this.open) {
      return this.close();
    }
    this.$refs.button.focus();

    this.open = true;
  }

  // Close dropdown
  close(focusAfter?: HTMLButtonElement) {
    if (!this.open) return;

    this.open = false;

    focusAfter && focusAfter.focus();
  }

  handleAddFilter(option: OptionItem, method: string) {
    if (method === "Toggle") {
      this.selectedFilters = option.checked
        ? [...this.selectedFilters, option]
        : this.selectedFilters.filter(
          (item: OptionItem) => item.value !== option.value
        );
    } else if (method === "Only") {
      this.selectedFilters = [option];
      this.close();
    } else this.selectedFilters = [];
  }

  rangePicker() {
    const picker = flatpickr(this.$refs.button, {
      mode: "range",
      dateFormat: "Y/m/d",
      onChange: (date: Date[]) => {
        const startDate = date[1] ? new Date(date[0]) : null;
        const endDate = date[1] ? new Date(date[1]) : null;
        const formatDate = (date: Date | null) => {
          if (!date) return '';
          return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
        };
        const dateLabel = `${formatDate(startDate)} - ${formatDate(endDate)}`;
        if (startDate && endDate) {
          const option = {
            label: "Select from calendar",
            value: dateLabel,
            checked: true,
            from: startDate,
            to: endDate,
          };
          this.selectedFilters = [option];
          this.close();
          picker.destroy();
        }
      },
      onClose: (date: Date[]) => {
        if (
          date.length < 2 ||
          (new Date(date[0]) === new Date(this.selectedFilters[0].from || "") &&
            new Date(date[1]) === new Date(this.selectedFilters[0].to || ""))
        )
          picker.destroy();
      },
    });
    this.close();
    picker.open();
  }
}
